import type { CompositionSide, CompositionUnit, OpoParagraph, UnitRole } from "../../api/types";
import { k, t } from "../../i18n/i18n";
import { roleName } from "../../map/units/useUnitMoves";
import { ENI_ROLES } from "../orbats/eni";

/** Rôles des unités dans la composition du dossier (docs/18 §2) : un seul vocabulaire pour les deux camps, et la
 *  correspondance de chaque rôle avec les paragraphes de l'OPO, dite en clair dans les sélecteurs (« 1er échelon —
 *  ENI initial dans l'OPO »). Fonctions pures, testées par roles.test.ts. */

export const UNIT_ROLES: readonly UnitRole[] = ENI_ROLES;

/** Rôles de la manœuvre (les autres ne tiennent pas d'emplacement de mêlée). */
export const MANOEUVRE_ROLES: readonly UnitRole[] = ["reco", "ech1", "ech2", "renforts"];

/** Nom court d'un rôle (clé de traduction). */
const SHORT: Record<UnitRole, string> = {
  reco: k("Reconnaissance"),
  ech1: k("1er échelon"),
  ech2: k("2e échelon"),
  renforts: k("Renforts"),
  appuis: k("Appuis"),
  soutien: k("Soutien"),
  commandement: k("Commandement"),
};

/** Rôle ennemi et paragraphe de l'OPO qui cite l'unité (docs/18 §2.2, correspondance D1 retenue). */
const ENI_OPTION: Record<UnitRole, string> = {
  reco: k("Reconnaissance — ENI initial (éléments de reconnaissance) dans l'OPO"),
  ech1: k("1er échelon — ENI initial dans l'OPO"),
  ech2: k("2e échelon — ENI ultérieur dans l'OPO"),
  renforts: k("Renforts — ENI futur dans l'OPO"),
  appuis: k("Appuis — appuis ENI dans l'OPO"),
  soutien: k("Soutien — non cité dans l'OPO"),
  commandement: k("Commandement — non cité dans l'OPO"),
};

/** Effet d'un rôle ami, en aide sous l'option. */
const AMI_HINT: Record<UnitRole, string> = {
  reco: k("En tête du dispositif, en avant du 1er échelon ; l'OPO la cite en reconnaissance"),
  ech1: k("Au contact ; l'OPO la cite au 1er échelon"),
  ech2: k("Engagée ensuite ; l'OPO la cite au 2e échelon"),
  renforts: k("En réserve ; l'OPO la cite en renforts"),
  appuis: k("Appuie la manœuvre (§3.c)"),
  soutien: k("Placée en zone de soutien (§4)"),
  commandement: k("Non citée dans les échelons de l'OPO"),
};

/** Paragraphes de l'OPO (correspondance affichée). */
const PARAGRAPH: Record<Exclude<OpoParagraph, null>, string> = {
  eni_reco: k("ENI initial (reconnaissance)"),
  eni_initial: k("ENI initial"),
  eni_ulterieur: k("ENI ultérieur"),
  eni_futur: k("ENI futur"),
  eni_appuis: k("Appuis ENI"),
  ami_reco: k("Reconnaissance"),
  ami_ech1: k("1er échelon"),
  ami_ech2: k("2e échelon"),
  ami_renforts: k("Renforts"),
  ami_appuis: k("Appuis (§3.c)"),
  ami_soutien: k("Soutien (§4)"),
};

/** Rôles d'une défense (rectificatif d'Alexis du 26/10/2026, registre A126) : « Quand on est en défensif, il n'y a
 *  qu'un 1er échelon défensif, appuyé par un 2ème échelon défensif, éventuellement renforcé, avec de la réserve. »
 *  Ni reconnaissance (pion de tête) ni échelons d'une attaque ; une reconnaissance choisie avant se lit au 1er échelon
 *  défensif (le serveur l'y range). */
const DEFENSIVE_SHORT: Partial<Record<UnitRole, string>> = {
  reco: k("1er échelon défensif"),
  ech1: k("1er échelon défensif"),
  ech2: k("2e échelon défensif"),
};
/** Aide d'un rôle d'une défense : la place de l'unité dans le dispositif, jamais sa mission (qui suit l'ordre). */
const DEFENSIVE_HINT: Partial<Record<UnitRole, string>> = {
  reco: k("À l'avant du dispositif défensif ; l'OPO la cite au 1er échelon"),
  ech1: k("À l'avant du dispositif défensif ; l'OPO la cite au 1er échelon"),
  ech2: k("En profondeur, derrière le 1er échelon défensif ; l'OPO la cite au 2e échelon"),
  renforts: k("En renforcement ou en réserve de la défense ; l'OPO la cite en renforts"),
};
const DEFENSIVE_PARAGRAPH: Partial<Record<Exclude<OpoParagraph, null>, string>> = {
  ami_reco: k("1er échelon défensif"),
  ami_ech1: k("1er échelon défensif"),
  ami_ech2: k("2e échelon défensif"),
};
const DEFENSIVE_ENGINE: Record<string, string> = {
  recoAmi: k("1er échelon défensif"),
  ech1Ami: k("1er échelon défensif"),
  ech2Ami: k("2e échelon défensif"),
};

/** Rôles amis proposés : en défensive (`defensive`, registre A126), sans la reconnaissance. */
export function amiRoles(roles: readonly UnitRole[], defensive: boolean): readonly UnitRole[] {
  return defensive ? roles.filter((role) => role !== "reco") : roles;
}

/** Rôle affiché dans un sélecteur ami : en défensive, une reconnaissance choisie avant se lit au 1er échelon défensif. */
export function shownRole(role: UnitRole | null, defensive: boolean): UnitRole | null {
  return defensive && role === "reco" ? "ech1" : role;
}

/** Niveau compagnie (docs/21 §4) : les rôles sont ceux des emplacements du S/GTIA, nommés comme dans son ordre
 *  (l'emplacement des renforts est l'appui ; la reconnaissance, le pion de tête) ; pas de réserve distincte. */
const COMPANY_SHORT: Partial<Record<UnitRole, string>> = {
  reco: k("Pion de tête"),
  renforts: k("Appui"),
};
const COMPANY_HINT: Partial<Record<UnitRole, string>> = {
  reco: k("En tête du dispositif (reconnaissance) ; l'OPO le cite en pion de tête"),
  ech1: k("L'effort ou le 2e pion, au contact ; l'OPO le cite au 1er échelon"),
  ech2: k("En mesure de reprendre l'effort, de recueillir ou de contre-attaquer ; tient lieu de réserve"),
  renforts: k("Base de feux ou avec l'effort ; l'OPO le cite en appui"),
};
const COMPANY_PARAGRAPH: Partial<Record<Exclude<OpoParagraph, null>, string>> = {
  ami_reco: k("Pion de tête"),
  ami_renforts: k("Appui"),
};
const COMPANY_ENGINE: Record<string, string> = { recoAmi: k("pion de tête"), renfortsAmi: k("appui") };

/** Rôles proposés au niveau compagnie : ceux qui ont un emplacement dans le dispositif du S/GTIA (publiés par le
 *  serveur), dans l'ordre de `UNIT_ROLES` ; à défaut, les rôles de la manœuvre. */
export function companyRoles(available: readonly UnitRole[] | null | undefined): readonly UnitRole[] {
  const allowed = available && available.length > 0 ? available : MANOEUVRE_ROLES;
  return UNIT_ROLES.filter((role) => allowed.includes(role));
}

/** Paragraphe de l'OPO qu'alimente un rôle, pour un camp (null : non cité dans les échelons). */
export function paragraphOf(side: CompositionSide, role: UnitRole | null): OpoParagraph {
  if (!role) return null;
  if (side === "ENI") {
    const eni: Partial<Record<UnitRole, OpoParagraph>> = {
      reco: "eni_reco",
      ech1: "eni_initial",
      ech2: "eni_ulterieur",
      renforts: "eni_futur",
      appuis: "eni_appuis",
    };
    return eni[role] ?? null;
  }
  const ami: Partial<Record<UnitRole, OpoParagraph>> = {
    reco: "ami_reco",
    ech1: "ami_ech1",
    ech2: "ami_ech2",
    renforts: "ami_renforts",
    appuis: "ami_appuis",
    soutien: "ami_soutien",
  };
  return ami[role] ?? null;
}

/** Nom court d'un rôle (« 1er échelon »), dans la langue de l'interface ; vide pour un rôle absent. `company` :
 *  vocabulaire du S/GTIA (« Appui », « Pion de tête ») ; `defensive` : rôles d'une défense (registre A126), côté ami
 *  seulement. */
export function roleShort(role: UnitRole | null | undefined, company = false, defensive = false): string {
  if (!role) return "";
  const own = (defensive ? DEFENSIVE_SHORT[role] : undefined) ?? (company ? COMPANY_SHORT[role] : undefined);
  return t(own ?? SHORT[role]);
}

/** Libellé d'un paragraphe de l'OPO (« ENI ultérieur ») ; vide pour null. */
export function paragraphLabel(paragraph: OpoParagraph | null | undefined, company = false, defensive = false): string {
  if (!paragraph) return "";
  const own = (defensive ? DEFENSIVE_PARAGRAPH[paragraph] : undefined) ?? (company ? COMPANY_PARAGRAPH[paragraph] : undefined);
  return t(own ?? PARAGRAPH[paragraph]);
}

/** Option du sélecteur de rôle : libellé (avec la correspondance OPO côté ennemi) et aide sous l'option (côté ami). */
export function roleOption(
  side: CompositionSide,
  role: UnitRole,
  company = false,
  defensive = false,
): { label: string; hint: string | null } {
  if (side === "ENI") return { label: t(ENI_OPTION[role]), hint: null };
  const hint = (defensive ? DEFENSIVE_HINT[role] : undefined) ?? (company ? COMPANY_HINT[role] : undefined);
  return { label: roleShort(role, company, defensive), hint: t(hint ?? AMI_HINT[role]) };
}

/** Rôle calculé par le moteur pour une unité amie au rôle automatique (« 1er échelon », « appui feux »…). */
export function engineRoleName(
  unit: Pick<CompositionUnit, "engine_role" | "support">,
  company = false,
  defensive = false,
): string | null {
  if (defensive && !unit.support && unit.engine_role && DEFENSIVE_ENGINE[unit.engine_role])
    return t(DEFENSIVE_ENGINE[unit.engine_role]!);
  if (company && unit.engine_role && COMPANY_ENGINE[unit.engine_role]) return t(COMPANY_ENGINE[unit.engine_role]!);
  if (unit.support) {
    const support: Record<string, string> = {
      artillerie: k("appui feux"),
      génie: k("appui génie"),
      soutien: k("soutien"),
      réserve: k("réserve"),
    };
    const label = support[unit.support];
    if (label) return t(label);
  }
  return roleName(unit.engine_role) ?? null;
}

/** Option « Automatique » d'une unité amie : d'après la distance à l'ennemi, avec le rôle calculé s'il est connu.
 *  Niveau compagnie (`company`, docs/21 §4) : les rôles sont ceux des emplacements, jamais recalculés d'après la
 *  distance à l'ennemi. */
export function autoOption(unit: Pick<CompositionUnit, "engine_role" | "support">, company = false, defensive = false): string {
  const current = engineRoleName(unit, company, defensive);
  if (company)
    return current
      ? t("Automatique — rôle de l'emplacement (actuellement : {role})", { role: current })
      : t("Automatique — rôle de l'emplacement");
  return current
    ? t("Automatique — d'après la distance à l'ennemi (actuellement : {role})", { role: current })
    : t("Automatique — d'après la distance à l'ennemi");
}

/** Repère du rôle par défaut dans un sélecteur, selon la provenance de l'unité ; face à l'ennemi générique
 *  (`neutral`, registre B39), une unité par défaut n'a pas de « rôle de référence ». */
export function defaultMark(unit: Pick<CompositionUnit, "origin">, neutral = false): string {
  if (unit.origin === "reference") return neutral ? t("Rôle par défaut") : t("Rôle de référence");
  if (unit.origin === "added") return t("Rôle par défaut");
  return t("Rôle de l'ordre de bataille");
}

/** Rôle affiché d'une unité : choisi, par défaut, ou automatique (ami). */
export function roleText(
  unit: Pick<CompositionUnit, "side" | "role" | "engine_role" | "support">,
  company = false,
  defensive = false,
): string {
  const ami = unit.side === "AMI" && defensive;
  if (unit.role) return roleShort(unit.role, company, ami);
  if (unit.side === "AMI") return engineRoleName(unit, company, defensive) ?? t("Automatique");
  return "";
}

/** Correspondance d'une unité dans l'OPO, en clair (« ENI ultérieur dans l'OPO ») ; vide si elle n'y est pas citée. */
export function opoMention(unit: Pick<CompositionUnit, "side" | "role" | "opo">, company = false, defensive = false): string {
  const paragraph = unit.opo ?? paragraphOf(unit.side, unit.role);
  const ami = unit.side === "AMI" && defensive;
  return paragraph ? t("{paragraph} dans l'OPO", { paragraph: paragraphLabel(paragraph, company, ami) }) : "";
}

/** Rôle à envoyer : null quand c'est le rôle par défaut (il n'est pas stocké, docs/18 §1.2). */
export function roleToSend(role: UnitRole | null, roleDefault: UnitRole | null): UnitRole | null {
  return role === roleDefault ? null : role;
}
